import {
  setDoc,
  updateDoc,
  doc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../lib/firebase";
import type {
  EventConfig,
  EventCreate,
  DeleteEventInput,
  DeleteEventResponse,
  InviteToEventInput,
  InviteToEventResponse,
  BulkCreateEventsInput,
  BulkCreateEventsResponse,
  BulkResolveEventsInput,
  BulkResolveEventsResponse,
  DuplicateEventInput,
  DuplicateEventResponse,
} from "@event-planner/shared";

/**
 * ADR-191: admins access every event through the admin claim and are
 * seated on an event only when an organizer invites them. So the creator
 * self-join below runs for organizers (who need a participation row for
 * access) and is skipped for admins. Read from the ID token, the same
 * claim the security rules check.
 */
async function callerIsAdmin(): Promise<boolean> {
  const token = await auth.currentUser?.getIdTokenResult();
  return token?.claims.role === "admin";
}

const deleteEventCallable = httpsCallable<
  DeleteEventInput,
  DeleteEventResponse
>(functions, "deleteEvent");

const inviteToEventCallable = httpsCallable<
  InviteToEventInput,
  InviteToEventResponse
>(functions, "inviteToEvent");

const bulkCreateEventsCallable = httpsCallable<
  BulkCreateEventsInput,
  BulkCreateEventsResponse
>(functions, "bulkCreateEvents");

const bulkResolveEventsCallable = httpsCallable<
  BulkResolveEventsInput,
  BulkResolveEventsResponse
>(functions, "bulkResolveEvents");

const duplicateEventCallable = httpsCallable<
  DuplicateEventInput,
  DuplicateEventResponse
>(functions, "duplicateEvent");

/**
 * Bulk-create events from the organizer-workbook upload. Inputs are
 * pre-validated by the admin's eventImportSpec + collapseEventRows; the
 * callable handles admin-only authorization, duplicate-name detection,
 * and the venue eventLocation companion-write.
 */
export async function bulkCreateEvents(
  input: BulkCreateEventsInput,
): Promise<BulkCreateEventsResponse> {
  const result = await bulkCreateEventsCallable(input);
  return result.data;
}

/**
 * Preview pass for the admin import dialog - returns per-row
 * proposals without writing.
 */
export async function bulkResolveEvents(
  input: BulkResolveEventsInput,
): Promise<BulkResolveEventsResponse> {
  const result = await bulkResolveEventsCallable(input);
  return result.data;
}

/**
 * Per-event theming (custom colors / fonts) is still deferred. Hero and
 * logo image uploads are wired through the Branding section of the
 * EventForm in edit mode (Issue #30).
 */
type DeferredMediaFields = "customTheme" | "customFonts";

/**
 * Shape the admin EventForm passes to `createEvent`. Derived from the
 * shared `EventCreate` type minus the still-deferred theme fields.
 */
export type CreateEventInput = Omit<EventCreate, DeferredMediaFields>;

type EventConfigPatch = {
  config?: EventConfig;
  "config.agendaLabel"?: EventConfig["agendaLabel"];
  "config.myScheduleLabel"?: EventConfig["myScheduleLabel"];
  "config.speakersLabel"?: EventConfig["speakersLabel"];
  "config.sessionsLabel"?: EventConfig["sessionsLabel"];
  "config.sessionTypes"?: EventConfig["sessionTypes"];
  "config.faqs"?: EventConfig["faqs"];
};

export type UpdateEventInput =
  Partial<Omit<CreateEventInput, "createdByUserId" | "config">> &
    EventConfigPatch;

export async function createEvent(
  input: CreateEventInput,
  /** Pre-generated Firestore doc ID. When omitted a random ID is used. */
  eventId?: string,
  /** Creator's email — needed for the inviteToEvent self-join. */
  creatorEmail?: string,
): Promise<string> {
  const docRef = eventId
    ? doc(db, "events", eventId)
    : doc(collection(db, "events"));

  await setDoc(docRef, {
    ...input,
    // Deferred theming — see DeferredMediaFields above
    customTheme: null,
    customFonts: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Organizer creators self-join so they can manage the event immediately
  // (the inviteToEvent callable allows event creators to add themselves
  // without an existing membership). Admins are not seated on events they
  // create (ADR-191); the claim already grants them everything.
  if (creatorEmail && !(await callerIsAdmin())) {
    await inviteToEventCallable({
      email: creatorEmail,
      eventId: docRef.id,
    });
  }

  return docRef.id;
}

/**
 * Duplicate an event "minus attendees" via the `duplicateEvent`
 * callable (event doc + locations + sessions + custom groups with empty
 * membership; schedule shifted to `newStartDate`). Must be a callable —
 * security rules block clients from creating named audiences.
 *
 * Mirrors createEvent's post-create ritual against the COPY: an organizer
 * caller self-joins via inviteToEvent (the callable stamps
 * `createdByUserId` = caller, so the creator self-join exception
 * applies). Admins are not seated on the copy (ADR-191); the claim
 * grants them access under the security rules.
 */
export async function duplicateEvent(
  input: DuplicateEventInput,
  /** Caller's email — needed for the organizer self-join. */
  creatorEmail?: string,
): Promise<DuplicateEventResponse> {
  const result = await duplicateEventCallable(input);

  if (creatorEmail && !(await callerIsAdmin())) {
    await inviteToEventCallable({
      email: creatorEmail,
      eventId: result.data.newEventId,
    });
  }

  return result.data;
}

export async function updateEvent(
  eventId: string,
  input: UpdateEventInput,
): Promise<void> {
  await updateDoc(doc(db, "events", eventId), {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cascade-delete an event through the `deleteEvent` callable (ADR-018).
 * Direct `deleteDoc` on `events/{id}` is blocked by security rules so that
 * every session + its descendants, plus eventMemberships, sponsors,
 * notifications, receipts, invitations, and email deliveries are removed
 * in the same pass.
 */
export async function deleteEvent(eventId: string): Promise<void> {
  await deleteEventCallable({ eventId });
}
